/**
 * GBなび レポート デモページ（週報/月報の確認用）
 *
 * ダミーの週次/月次サマリを生成し、TerasuReport に注入して表示する。
 * シナリオ(良好/混在/厳しい)と在籍者数を切り替えて、天気判定の挙動を確認できる。
 *
 * 注: デモ専用。天気判定式は本番(terasu-weather.ts)と同一、回答データはランダム。
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Slider,
  Typography,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { TerasuReport } from './TerasuReport';
import { generateDummyTerasuReport, type TerasuScenario } from '../utils/generateDummyTerasuReport';

/** テーマ切替の選択肢 (今月のテーマ→取り組み候補の確認用) */
const THEME_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'policy_info', label: '方針と情報' },
  { key: 'meeting', label: '会議と決まり方' },
  { key: 'dept_link', label: '部門連携' },
  { key: 'development', label: '育成と戦力化' },
  { key: 'improvement', label: '改善と変化対応' },
  { key: 'customer_value', label: '顧客価値と一体感' },
];

export const TerasuReportDemoPage: React.FC = () => {
  const [scenario, setScenario] = useState<TerasuScenario>('mixed');
  const [members, setMembers] = useState(20);
  const [themeKey, setThemeKey] = useState('dept_link');
  const [seed, setSeed] = useState(0);

  const data = useMemo(
    () => generateDummyTerasuReport({ scenario, memberCount: members, themeKey }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scenario, members, themeKey, seed]
  );

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', px: 2, py: 3 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          GBなび レポート デモ（ダミー回答）
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          ダミーの週次/月次サマリを生成し、週報・月報を描画します。天気（☀⛅🌧⚡）の判定は本番と同じ閾値、
          回答データはランダムなデモ用です。
        </Typography>
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" gutterBottom>
              組織の状態シナリオ
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={scenario}
              onChange={(_, v) => v && setScenario(v)}
            >
              <ToggleButton value="good">良好</ToggleButton>
              <ToggleButton value="mixed">混在</ToggleButton>
              <ToggleButton value="tough">厳しい</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography variant="body2" gutterBottom>
              今月のテーマ（取り組み候補の確認用）
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={themeKey}
              onChange={(_, v) => v && setThemeKey(v)}
              sx={{ flexWrap: 'wrap' }}
            >
              {THEME_OPTIONS.map((t) => (
                <ToggleButton key={t.key} value={t.key}>
                  {t.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
            <Box sx={{ minWidth: 240 }}>
              <Typography variant="body2" gutterBottom>
                在籍者数: <strong>{members}名</strong>
              </Typography>
              <Slider
                value={members}
                min={7}
                max={60}
                step={1}
                marks={[
                  { value: 7, label: '7' },
                  { value: 20, label: '20' },
                  { value: 40, label: '40' },
                  { value: 60, label: '60' },
                ]}
                onChange={(_, v) => setMembers(v as number)}
                valueLabelDisplay="auto"
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => setSeed((s) => s + 1)}
            >
              ダミーを再生成
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <TerasuReport data={data} />
    </Box>
  );
};

export default TerasuReportDemoPage;
